/**
 * MetadataModal — Exibe o campo JSONB `data` de qualquer entidade.
 * Mostra migration metadata, flags, e raw data de forma estruturada.
 */
import { FaTimes, FaFlag, FaDatabase, FaInfoCircle } from 'react-icons/fa';

interface Props {
    data: Record<string, any> | null;
    title?: string;
    onClose: () => void;
}

const MetadataModal: React.FC<Props> = ({ data, title = 'Metadata', onClose }) => {
    if (!data) return null;

    const flags = data.flags || null;
    const migration = data.migration || null;
    const rest = Object.fromEntries(
        Object.entries(data).filter(([k]) => !['flags', 'migration'].includes(k))
    );

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                        <FaDatabase className="text-blue-500" />
                        {title}
                    </h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                        <FaTimes size={20} />
                    </button>
                </div>

                {/* Flags Section */}
                {flags && (
                    <div className={`mb-4 p-3 rounded-lg border ${flags.overpaid ? 'bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-700' : 'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-700'}`}>
                        <h3 className="text-sm font-semibold flex items-center gap-2 mb-2 text-amber-700 dark:text-amber-300">
                            <FaFlag className="text-amber-500" />
                            Flags
                        </h3>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                            {Object.entries(flags).map(([key, value]) => (
                                <div key={key} className="flex justify-between">
                                    <span className="text-gray-600 dark:text-gray-400">{key}:</span>
                                    <span className="font-mono text-gray-900 dark:text-gray-100">
                                        {typeof value === 'number'
                                            ? value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
                                            : String(value)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Migration Section */}
                {migration && (
                    <div className="mb-4 p-3 rounded-lg border bg-slate-50 border-slate-200 dark:bg-slate-700/30 dark:border-slate-600">
                        <h3 className="text-sm font-semibold flex items-center gap-2 mb-2 text-slate-700 dark:text-slate-300">
                            <FaInfoCircle className="text-slate-500" />
                            Migração
                        </h3>
                        <div className="text-xs space-y-1">
                            {migration.batch_id && (
                                <div className="flex gap-2">
                                    <span className="text-gray-500">Batch:</span>
                                    <span className="font-mono">{migration.batch_id}</span>
                                </div>
                            )}
                            {migration.migrated_at && (
                                <div className="flex gap-2">
                                    <span className="text-gray-500">Data:</span>
                                    <span className="font-mono">{new Date(migration.migrated_at).toLocaleString('pt-BR')}</span>
                                </div>
                            )}
                            {migration.source && (
                                <div className="flex gap-2">
                                    <span className="text-gray-500">Origem:</span>
                                    <span className="font-mono">{migration.source.database}.{migration.source.schema}.{migration.source.table}</span>
                                </div>
                            )}
                        </div>
                        {/* Source Row — original data */}
                        {migration.source_row && (
                            <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-600">
                                <h4 className="text-xs font-semibold text-slate-600 dark:text-slate-400 mb-2">Dados Originais</h4>
                                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                                    {Object.entries(migration.source_row).map(([key, value]) => (
                                        <div key={key} className="flex justify-between py-0.5 border-b border-dotted border-slate-200 dark:border-slate-600">
                                            <span className="text-gray-500 truncate mr-2">{key}</span>
                                            <span className="font-mono text-gray-900 dark:text-gray-100 truncate text-right max-w-[200px]" title={String(value ?? '')}>
                                                {value === null ? <span className="text-gray-400 italic">null</span> : String(value)}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Raw Data Section */}
                {Object.keys(rest).length > 0 && (
                    <div className="p-3 rounded-lg border bg-gray-50 border-gray-200 dark:bg-gray-700/30 dark:border-gray-600">
                        <h3 className="text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">Dados</h3>
                        <pre className="text-xs font-mono whitespace-pre-wrap break-words text-gray-800 dark:text-gray-200 max-h-60 overflow-y-auto">
                            {JSON.stringify(rest, null, 2)}
                        </pre>
                    </div>
                )}

                {/* Empty state */}
                {!flags && !migration && Object.keys(rest).length === 0 && (
                    <p className="text-gray-500 text-sm text-center py-4">Nenhum metadata disponível</p>
                )}
            </div>
        </div>
    );
};

export default MetadataModal;
